import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';

const COLORES_PLAN = {
  1: '#3b82f6', // Básico
  2: '#8b5cf6', // Profesional
  3: '#ec4899', // Empresarial
  4: '#f59e0b', // Enterprise
  5: '#6b7280', // Trial
};

export default function CambiarPlan() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);

  // State para el cambio
  const [planActual, setPlanActual] = useState(null);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [calculos, setCalculos] = useState(null);
  const [permitirArchival, setPermitirArchival] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [transactionId, setTransactionId] = useState(null);

  // Cargar info inicial
  useEffect(() => {
    const cargar = async () => {
      try {
        const [planesRes, estadoRes] = await Promise.all([
          api.get('/planes'),
          api.get('/planes/estado'),
        ]);
        setPlanes(planesRes.data);
        setPlanActual(estadoRes.data);
      } catch (error) {
        toast.error('Error al cargar información');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [navigate]);

  // Paso 1: Calcular prorrateo
  const handleSeleccionarPlan = async (plan) => {
    setEnviando(true);
    try {
      const { data } = await api.post('/payments/plan-change', {
        planId: plan.id,
        permitir_archival: false,
      });

      setCalculos(data);
      setPlanSeleccionado(plan);
      setTransactionId(data.transactionId || null);

      // Si hay conflictos, mostrar warning
      if (data.conflictos && data.conflictos.length > 0) {
        toast.warning('Tienes conflictos de recursos. Revisa las opciones.');
      }

      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al calcular prorrateo');
    } finally {
      setEnviando(false);
    }
  };

  // Paso 2: Confirmar cambio (con archival si aplica)
  const handleConfirmarCambio = async () => {
    if (calculos?.conflictos?.length > 0 && !permitirArchival) {
      toast.error('Debes aceptar el archival de recursos');
      return;
    }

    setEnviando(true);
    try {
      if (calculos.requiere_pago) {
        // Si requiere pago, ir a checkout
        setStep(3);
      } else {
        // Si es crédito o sin pago, confirmar directamente
        const { data } = await api.post('/payments/confirm-plan-change', {
          planId: planSeleccionado.id,
          permitir_archival: permitirArchival,
        });
        toast.success(data.mensaje);
        setStep(4);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al confirmar');
    } finally {
      setEnviando(false);
    }
  };

  // Paso 3: Procesar pago
  const handleProcesarPago = async () => {
    if (!transactionId) {
      toast.error('No hay transacción');
      return;
    }

    setEnviando(true);
    try {
      // Aquí iría integración con Payphone
      // Por ahora, simular confirmación
      const { data } = await api.post('/payments/confirm-plan-change', {
        planId: planSeleccionado.id,
        transactionId: transactionId,
        permitir_archival: permitirArchival,
      });
      toast.success(data.mensaje);
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error en pago');
    } finally {
      setEnviando(false);
    }
  };

  // Ir atrás
  const handleVolver = () => {
    if (step > 1) setStep(step - 1);
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="loading" />
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Cargando...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        {/* Progress */}
        <div style={styles.progress}>
          {[1, 2, 3, 4].map(s => (
            <div key={s} style={{ textAlign: 'center', flex: 1 }}>
              <div
                style={{
                  ...styles.progressDot,
                  background: step >= s ? '#3b82f6' : '#e5e7eb',
                  color: step >= s ? '#fff' : '#9ca3af',
                }}
              >
                {s}
              </div>
              <div style={{ fontSize: '0.75rem', marginTop: '0.3rem', color: step === s ? '#3b82f6' : '#9ca3af' }}>
                {['Plan', 'Revisar', 'Pago', 'Confirmar'][s - 1]}
              </div>
            </div>
          ))}
        </div>

        {/* PASO 1: Seleccionar plan */}
        {step === 1 && (
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <h2 style={styles.title}>¿Qué plan quieres?</h2>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
              Plan actual: <strong>{planActual?.plan?.nombre}</strong> (${parseFloat(planActual?.plan?.precio || 0).toFixed(2)}/mes)
            </p>

            <div className="grid-responsive" style={{ gap: '1rem', marginBottom: '1.5rem' }}>
              {planes
                .filter(p => p.id !== planActual?.plan?.id && p.nombre !== 'Trial')
                .map(plan => (
                  <button
                    key={plan.id}
                    onClick={() => handleSeleccionarPlan(plan)}
                    disabled={enviando}
                    style={{
                      ...styles.planCard,
                      borderColor: COLORES_PLAN[plan.id],
                      opacity: enviando ? 0.5 : 1,
                    }}
                  >
                    <div style={{ fontSize: '1.25rem', fontWeight: '800', color: COLORES_PLAN[plan.id] }}>
                      {plan.nombre}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#111827', marginTop: '0.5rem' }}>
                      ${parseFloat(plan.precio_mensual).toFixed(2)}
                      <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>/mes</span>
                    </div>
                    {plan.descripcion && (
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.75rem 0 0' }}>
                        {plan.descripcion}
                      </p>
                    )}
                  </button>
                ))}
            </div>

            <button onClick={() => navigate('/configuracion')} style={styles.btnSecondary}>
              ← Volver
            </button>
          </div>
        )}

        {/* PASO 2: Revisar cambio */}
        {step === 2 && calculos && (
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <h2 style={styles.title}>Revisar cambio</h2>

            {/* Resumen comparativa */}
            <div style={styles.comparativa}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>Plan actual</div>
                <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#111827', marginTop: '0.25rem' }}>
                  {calculos.plan_actual.nombre}
                </div>
                <div style={{ fontSize: '1rem', color: '#3b82f6', marginTop: '0.5rem' }}>
                  ${calculos.plan_actual.precio.toFixed(2)}/mes
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d1d5db', fontWeight: '700' }}>
                →
              </div>

              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>Nuevo plan</div>
                <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#111827', marginTop: '0.25rem' }}>
                  {calculos.plan_nuevo.nombre}
                </div>
                <div style={{ fontSize: '1rem', color: '#3b82f6', marginTop: '0.5rem' }}>
                  ${calculos.plan_nuevo.precio.toFixed(2)}/mes
                </div>
              </div>
            </div>

            {/* Detalles de prorrateo */}
            <div style={styles.detallesBox}>
              <div style={styles.detallesRow}>
                <span>Días restantes en ciclo</span>
                <span style={{ fontWeight: '700' }}>{calculos.dias_restantes} días</span>
              </div>
              <div style={styles.detallesRow}>
                <span>Diferencia de precio</span>
                <span style={{ fontWeight: '700' }}>
                  ${Math.abs(calculos.plan_nuevo.precio - calculos.plan_actual.precio).toFixed(2)}/mes
                </span>
              </div>
              <div style={{ ...styles.detallesRow, borderTop: '2px solid #e5e7eb', paddingTop: '0.75rem' }}>
                <span style={{ fontWeight: '700' }}>
                  {calculos.monto_prorrateo > 0 ? 'Monto a pagar:' : calculos.monto_prorrateo < 0 ? 'Crédito:' : 'Total:'}
                </span>
                <span
                  style={{
                    fontWeight: '900',
                    fontSize: '1.25rem',
                    color: calculos.monto_prorrateo > 0 ? '#dc2626' : calculos.monto_prorrateo < 0 ? '#16a34a' : '#3b82f6',
                  }}
                >
                  {calculos.monto_prorrateo >= 0 ? '+' : ''}${Math.abs(calculos.monto_prorrateo).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Conflictos de recurso (si aplica) */}
            {calculos.conflictos && calculos.conflictos.length > 0 && (
              <div style={styles.alertaConflictos}>
                <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#92400e', marginBottom: '0.75rem' }}>
                  ⚠️ {calculos.plan_nuevo.nombre} soporta menos recursos
                </div>
                {calculos.conflictos.map((conflicto, idx) => (
                  <div key={idx} style={{ fontSize: '0.85rem', color: '#78350f', marginBottom: '0.5rem' }}>
                    <strong>{conflicto.tipo}:</strong> Tienes {conflicto.cantidad}, límite {conflicto.limite}
                  </div>
                ))}

                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={permitirArchival}
                    onChange={e => setPermitirArchival(e.target.checked)}
                  />
                  <span style={{ fontSize: '0.9rem', color: '#111827' }}>
                    Permitir archival automático de{' '}
                    {calculos.conflictos.reduce((acc, c) => acc + (c.archivables || 0), 0)} recursos
                  </span>
                </label>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button
                onClick={handleVolver}
                style={{ ...styles.btnSecondary, flex: 1 }}
                disabled={enviando}
              >
                ← Atrás
              </button>
              <button
                onClick={handleConfirmarCambio}
                style={{ ...styles.btnPrimary, flex: 1, opacity: enviando ? 0.7 : 1 }}
                disabled={enviando || (calculos.conflictos?.length > 0 && !permitirArchival)}
              >
                {enviando ? 'Procesando...' : 'Continuar →'}
              </button>
            </div>
          </div>
        )}

        {/* PASO 3: Pago */}
        {step === 3 && calculos?.requiere_pago && (
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <h2 style={styles.title}>Completar pago</h2>

            <div style={styles.pagoBox}>
              <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem' }}>Monto a pagar:</div>
              <div style={{ fontSize: '2.5rem', fontWeight: '900', color: '#dc2626' }}>
                ${Math.abs(calculos.monto_prorrateo).toFixed(2)}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.75rem' }}>
                por {calculos.dias_restantes} días restantes
              </div>
            </div>

            <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#eff6ff', borderRadius: '10px' }}>
              <div style={{ fontSize: '0.9rem', color: '#1d4ed8', marginBottom: '0.5rem' }}>
                💳 Se procesará con Payphone
              </div>
              <div style={{ fontSize: '0.85rem', color: '#1e40af' }}>
                Serás redirigido a la pasarela de pago segura.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button
                onClick={handleVolver}
                style={{ ...styles.btnSecondary, flex: 1 }}
                disabled={enviando}
              >
                ← Atrás
              </button>
              <button
                onClick={handleProcesarPago}
                style={{ ...styles.btnPrimary, flex: 1, opacity: enviando ? 0.7 : 1 }}
                disabled={enviando}
              >
                {enviando ? 'Procesando...' : '💳 Pagar ahora'}
              </button>
            </div>
          </div>
        )}

        {/* PASO 4: Confirmación */}
        {step === 4 && calculos && (
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#111827', marginBottom: '0.5rem' }}>
                ¡Cambio completado!
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                Tu plan se ha actualizado correctamente
              </p>

              <div style={styles.confirmBox}>
                <div style={styles.confirmRow}>
                  <span style={{ color: '#6b7280' }}>Nuevo plan:</span>
                  <span style={{ fontWeight: '700', color: '#111827' }}>{calculos.plan_nuevo.nombre}</span>
                </div>
                {calculos.monto_prorrateo > 0 && (
                  <div style={styles.confirmRow}>
                    <span style={{ color: '#6b7280' }}>Monto cobrado:</span>
                    <span style={{ fontWeight: '700', color: '#dc2626' }}>
                      ${Math.abs(calculos.monto_prorrateo).toFixed(2)}
                    </span>
                  </div>
                )}
                {calculos.monto_prorrateo < 0 && (
                  <div style={styles.confirmRow}>
                    <span style={{ color: '#6b7280' }}>Crédito generado:</span>
                    <span style={{ fontWeight: '700', color: '#16a34a' }}>
                      ${Math.abs(calculos.monto_prorrateo).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <button
                onClick={() => navigate('/configuracion')}
                style={styles.btnPrimary}
              >
                Ir a configuración
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  progress: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
  },
  progressDot: {
    width: '2rem',
    height: '2rem',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    transition: 'all 0.2s',
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: '800',
    color: '#111827',
    margin: '0 0 1.5rem',
  },
  planesList: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  planCard: {
    border: '2px solid',
    borderRadius: '12px',
    padding: '1.25rem',
    background: '#fafafa',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.15s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
  },
  btnPrimary: {
    padding: '0.875rem 1.5rem',
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '0.95rem',
  },
  btnSecondary: {
    padding: '0.875rem 1.5rem',
    background: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: '10px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '0.95rem',
  },
  comparativa: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    margin: '1.5rem 0',
    padding: '1.25rem',
    background: '#f9fafb',
    borderRadius: '10px',
  },
  detallesBox: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '1rem',
    marginTop: '1.5rem',
  },
  detallesRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    fontSize: '0.9rem',
  },
  alertaConflictos: {
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: '10px',
    padding: '1rem',
    marginTop: '1.5rem',
    color: '#92400e',
  },
  pagoBox: {
    background: '#fee2e2',
    border: '2px solid #fca5a5',
    borderRadius: '10px',
    padding: '1.5rem',
    textAlign: 'center',
    marginTop: '1.5rem',
  },
  confirmBox: {
    background: '#f0fdf4',
    border: '1px solid #86efac',
    borderRadius: '10px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  confirmRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontSize: '0.95rem',
  },
};
